import { useState } from 'react'
import { useTranslation } from '../i18n'
import { useAuth } from '../contexts/AuthContext'
import { ExtractionManager } from './ExtractionManager'
import { Progress } from './ui/progress'
import { ExtractionStatus, ExtractionSession } from '../types/multi-extraction'
import { UseExtractionsResult } from '../hooks/useExtractions'

interface ExtractionsProps {
  extractions: UseExtractionsResult;
  onSelectSession?: (id: string | null) => void;
  selectedSessionId?: string | null;
  activeSessions?: ExtractionSession[];
}

export default function Extractions({ extractions, onSelectSession, selectedSessionId, activeSessions: propActiveSessions }: ExtractionsProps) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const {
    savedSessions,
    isLoadingSaved,
    savedError,
    deleteSavedSession,
    refreshSavedExtractions,
    activeSessions: hookActiveSessions
  } = extractions;

  const activeSessions = propActiveSessions || hookActiveSessions;

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    if (!confirm(t('saved_extractions.confirm_delete', 'Delete this extraction?'))) return;

    try {
      setDeletingId(id);
      await deleteSavedSession(id);
    } catch (error) {
      alert(`${t('saved_extractions.delete_failed', 'Failed to delete extraction:')} ${error instanceof Error ? error.message : t('saved_extractions.unknown_error','Unknown error')}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">{t('extractions.title', 'Extractions')}</h2>
        <div className="text-muted-foreground">{t('extractions.login_prompt', 'Please log in to view extractions')}</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('extractions.title', 'Extractions')}</h2>
        <button
          onClick={refreshSavedExtractions}
          disabled={isLoadingSaved}
          className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoadingSaved ? t('extractions.refreshing', 'Refreshing...') : t('extractions.refresh', 'Refresh')}
        </button>
      </div>

      {activeSessions.filter(session => session.status === ExtractionStatus.IN_PROGRESS).map(session => (
        <div key={session.id} className="mb-4 p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-950/20">
          <h3 className="text-lg font-semibold mb-2">{t('extractions.streaming_title', 'Streaming Extraction')}</h3>
          <div className="mb-2">{session.title}</div>
          <Progress value={session.progress.length > 0 ? session.progress[session.progress.length - 1].progress_percent : 0} />
          <div className="text-sm text-muted-foreground mt-1">
            {t('extractions.extracting', 'Extracting...')}
          </div>
        </div>
      ))}

  {isLoadingSaved && <div>{t('extractions.loading', 'Loading extractions...')}</div>}

      {savedError && (
        <div className="text-red-600 mb-4">
          {t('extractions.error_loading', 'Error loading extractions:')} {savedError}
        </div>
      )}

      {!isLoadingSaved && !savedError && savedSessions.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <div className="text-4xl mb-2">📄</div>
          <div className="text-sm">{t('extractions.none_yet', 'No saved extractions yet')}</div>
        </div>
      )}

      {!isLoadingSaved && !savedError && savedSessions.length > 0 && (
        <ExtractionManager
          sessions={savedSessions}
          selectedSessionId={selectedSessionId || null}
          activeSessionId={null}
          onSelectSession={onSelectSession || (() => {})}
          onDeleteSession={handleDelete}
          onCancelExtraction={() => {}} // Saved extractions can't be cancelled
          onUpdateTitle={() => {}} // Saved extractions titles are read-only
        />
      )}

      {deletingId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span>{t('extractions.deleting', 'Deleting extraction...')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
